import { FC, memo } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import FormField from '@amzn/awsui-components-core/form-field';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

export interface FundingAccountSectionProps extends SectionBaseProps {
    /** Currently selected funding account value */
    selectedFundingAccount: string;
    /** Callback when funding account selection changes */
    setSelectedFundingAccount: (value: string) => void;
    /** Available funding account options (pre-computed by parent) */
    fundingAccountOptions: SelectProps.Option[];
}

/**
 * Funding account section — renders the funding account dropdown.
 *
 * For NCEMI/DEAL: parent populates options from the selected merchant's cpgnAccounts.
 * For CB/BB: parent populates options from config.aggregatedCPGNAccounts.
 * This component doesn't need to know about merchant groups — it just renders
 * whatever options the parent passes in.
 * Owns its own i18n bundle (`organisms.fundingAccountSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const FundingAccountSection: FC<FundingAccountSectionProps> = ({
    selectedFundingAccount,
    setSelectedFundingAccount,
    fundingAccountOptions,
    mode,
    disabled,
}) => {
    const { translate } = useTranslate('organisms.fundingAccountSection');
    // Funding account is read-only after creation — disabled in both view and modify modes.
    const isDisabled = disabled ?? mode !== 'create';

    return (
        <Container header={<Header variant="h2">{translate('section_funding_account')}</Header>}>
            <FormField label={translate('funding_account_label')}>
                <Select
                    selectedOption={
                        fundingAccountOptions.find((o) => o.value === selectedFundingAccount) ?? null
                    }
                    onChange={({ detail }) =>
                        setSelectedFundingAccount(detail.selectedOption.value ?? '')
                    }
                    options={fundingAccountOptions}
                    placeholder={translate('funding_account_placeholder')}
                    ariaRequired={true}
                    disabled={isDisabled}
                    data-testid="funding-account"
                />
            </FormField>
        </Container>
    );
};

export default memo(FundingAccountSection);
